import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router';
import PropTypes from 'prop-types';

import Cookies from 'js-cookie';

// # DND KIT
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// # DRAFT JS
import { Editor } from 'react-draft-wysiwyg';
import { ContentState, convertFromHTML, convertToRaw, EditorState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';

// # MOMENT JS
import moment from 'moment';
import 'moment/locale/fr';

// # UTILS

// # MATERIAL UI
import {
  Grid,
  Divider,
  Paper,
  Input,
  FormControlLabel,
  Switch,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Autocomplete, Skeleton } from '@material-ui/lab';
import { useSnackbar } from 'notistack';
import { autocompleteGroupedFormater } from '../../../utils/stringFormater';
import { axiosGetRequest, axiosPatchRequest } from '../../../utils/axiosRequests';

const useStyles = makeStyles((theme) => ({
  paper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    marginBottom: '1rem',
    padding: '15px 30px 24px',
    outline: 'none',

    '& h4': {
      margin: '5px 0',
    },
  },
  marginBottom: {
    marginBottom: '1rem',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
  },
  divider: {
    margin: '1rem',
  },
  flexCenter: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  spaceBetween: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  infos: {
    '& > div': {
      display: 'flex',
      flexGrow: 1,

      '& > .MuiFormControl-root': {
        margin: '0 1rem 1rem 0',
        flexGrow: 1,
      },
    },

    '& h4': {
      marginBottom: '1rem',
    },
  },
  cellInput: {
    width: '80px',
    marginRight: '5px',
  },
  sortableRow: {
    cursor: 'grab',
    '&:active': {
      cursor: 'grabbing',
    },
  },
  dragHandle: {
    cursor: 'grab',
    padding: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:active': {
      cursor: 'grabbing',
    },
  },
}));

// # SORTABLE ROW COMPONENT
function SortableRow({ product, index, classes, state, handleProductsChange, weightUnities }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: index });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow ref={setNodeRef} style={style} className={classes.sortableRow} {...attributes}>
      <TableCell align="left">
        <div className={classes.dragHandle} {...listeners}>
          ⋮⋮
        </div>
      </TableCell>
      <TableCell align="left">
        <img src={`${process.env.REACT_APP_AWS}/${product.photo}`} alt={product.reference} width={70} />
      </TableCell>
      <TableCell align="left">
        <TextField
          name="name"
          type="text"
          variant="outlined"
          value={state.basket.products[index].name}
          onChange={(event) => handleProductsChange(event, index)}
        />
      </TableCell>
      <TableCell align="left">
        <TextField
          className={classes.cellInput}
          name="quantity"
          type="number"
          variant="outlined"
          value={product.weight.quantity}
          onChange={(event) => handleProductsChange(event, index)}
        />
        <FormControl variant="outlined">
          <InputLabel>Unité</InputLabel>
          <Select
            native
            name="unity"
            label="Unité"
            value={weightUnities.find((unity) => unity.name === state.basket.products[index].weight.unity)?.id}
            onChange={(event) => handleProductsChange(event, index)}
          >
            {weightUnities.map((unity, idx) => (
              <option key={idx} value={unity.id}>
                {unity.name}
              </option>
            ))}
          </Select>
        </FormControl>
      </TableCell>
      <TableCell align="left">
        <TextField
          name="origin"
          type="text"
          variant="outlined"
          value={state.basket.products[index].origin}
          onChange={(event) => handleProductsChange(event, index)}
        />
      </TableCell>
      <TableCell align="right">
        <div className={classes.column}>
          <FormControlLabel
            control={
              <Checkbox
                checked={state.basket.products[index].labels.organic}
                name="organic"
                onChange={(event) => handleProductsChange(event, index)}
              />
            }
            label="Bio"
            labelPlacement="start"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={state.basket.products[index].labels.wild}
                name="wild"
                onChange={(event) => handleProductsChange(event, index)}
              />
            }
            label="Sauvage"
            labelPlacement="start"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={state.basket.products[index].labels.french}
                name="french"
                onChange={(event) => handleProductsChange(event, index)}
              />
            }
            label="Français"
            labelPlacement="start"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={state.basket.products[index].labels.native}
                name="native"
                onChange={(event) => handleProductsChange(event, index)}
              />
            }
            label="Local"
            labelPlacement="start"
          />
        </div>
      </TableCell>
    </TableRow>
  );
}

SortableRow.propTypes = {
  product: PropTypes.shape({
    photo: PropTypes.string,
    reference: PropTypes.string,
    weight: PropTypes.shape({
      quantity: PropTypes.number,
      unity: PropTypes.string,
    }),
  }).isRequired,
  index: PropTypes.number.isRequired,
  classes: PropTypes.object.isRequired,
  state: PropTypes.shape({
    basket: PropTypes.shape({
      products: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          origin: PropTypes.string,
          labels: PropTypes.shape({
            organic: PropTypes.bool,
            wild: PropTypes.bool,
            french: PropTypes.bool,
            native: PropTypes.bool,
          }),
          weight: PropTypes.shape({
            unity: PropTypes.string,
          }),
        })
      ),
    }),
  }).isRequired,
  handleProductsChange: PropTypes.func.isRequired,
  weightUnities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    })
  ).isRequired,
};

export default function BasketPage() {
  moment.locale('fr');
  const classes = useStyles();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const { basketId } = useParams();

  // # DND SENSORS
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // # EDITOR DEFAULT STATE
  const editorData = (data) => {
    const contentDataState = ContentState.createFromBlockArray(convertFromHTML(data));

    return EditorState.createWithContent(contentDataState);
  };

  // # STATE
  const [state, setState] = useState({
    loading: true,
    products: [],
    taxes: [],
    tags: [],
    categories: [],
    subCategories: [],
    weightUnities: [],
    basket: {
      newPhoto: undefined,
      photo: undefined,
      name: undefined,
      reference: undefined,
      price: undefined,
      tvaRate: undefined,
      description: EditorState.createEmpty(),
      category: undefined,
      subCategory: undefined,
      isActive: true,
      isTop: false,
      isEco: false,
      size: 'SOLO',
      stock: 100,
      tags: [],
      weight: {
        from: undefined,
        to: undefined,
        unity: undefined,
      },
      persons: {
        from: 2,
        to: 3,
      },
      availability: {
        from: moment().format('YYYY-MM-DD'),
        to: moment().add(1, 'weeks').format('YYYY-MM-DD'),
      },
      labels: {
        organic: false,
        wild: false,
        french: false,
        native: false,
      },
      products: [],
    },
  });

  // # FETCH DATAS
  useEffect(() => {
    const token = Cookies.get('jwt');

    // # FETCH PRODUCTS
    axiosGetRequest('/products', token).then((res) => {
      const { products } = res.data.data;
      const productFormat = autocompleteGroupedFormater(products, 'name');
      setState((prev) => ({
        ...prev,
        products: [...productFormat],
      }));
    });

    // # FETCH TAXES
    axiosGetRequest('/taxes/', token).then((res) => {
      const { taxes } = res.data.data;
      setState((prev) => ({
        ...prev,
        taxes,
      }));
    });

    // # FETCH CATEGORIES
    axiosGetRequest('/categories/', token).then((res) => {
      const { categories } = res.data.data;
      setState((prev) => ({
        ...prev,
        categories,
      }));
    });

    // # FETCH SUBCATEGORIES
    axiosGetRequest('/subcategories/', token).then((res) => {
      const { subCategories } = res.data.data;
      setState((prev) => ({
        ...prev,
        subCategories,
      }));
    });

    // # FETCH TAGS
    axiosGetRequest('/tags/', token).then((res) => {
      const { tags } = res.data.data;
      const formattedTags = tags.map((tag) => ({
        ...tag,
        cleanName: tag.name.toLowerCase().split('.')[1],
      }));
      const tagsFormat = autocompleteGroupedFormater(formattedTags, 'cleanName');
      setState((prev) => ({
        ...prev,
        tags: [...tagsFormat],
      }));
    });

    // # FETCH WEIGHT UNITIES
    axiosGetRequest('/weight/', token).then((res) => {
      const { unities } = res.data.data;
      setState((prev) => ({
        ...prev,
        weightUnities: unities,
      }));
    });

    // # FETCH BASKET BY ID
    axiosGetRequest(`/baskets/${basketId}`).then((res) => {
      const { basket } = res.data.data;
      setState((prev) => ({
        ...prev,
        loading: false,
        basket: {
          ...prev.basket,
          ...basket,
          category: basket.category.id,
          subCategory: basket.subCategory.id,
          weight: {
            ...basket.weight,
            unity: basket.weight.unity.id,
          },
          tvaRate: basket.tvaRate.id,
          description: editorData(basket.description),
        },
      }));
    });
  }, [basketId]);

  // # HANDLE NEW IMG UPLOAD
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setState((prev) => ({
      ...prev,
      basket: {
        ...prev.basket,
        newPhoto: file,
      },
    }));
  };

  // # HANDLE SWITCH CHANGES
  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setState((prev) => ({
      ...prev,
      basket: {
        ...prev.basket,
        [name]: checked,
      },
    }));
  };

  // # HANDLE SWITCH LABELS CHANGES
  const handleSwitchLabelsChange = (e) => {
    const { name, checked } = e.target;
    setState((prev) => ({
      ...prev,
      basket: {
        ...prev.basket,
        labels: {
          ...prev.basket.labels,
          [name]: checked,
        },
      },
    }));
  };

  // # HANDLE INPUTS CHANGES
  const handleInputsChange = (e) => {
    e.preventDefault();
    let { name, value, type } = e.target;
    if (type === 'number') value *= 1;

    setState((prev) => ({
      ...prev,
      basket: {
        ...prev.basket,
        [name]: value,
      },
    }));
  };

  // # HANDLE WEIGHT INPUTS CHANGES
  // TODO : NEED REFACTORING
  const handleInputsWeightChange = (e) => {
    e.preventDefault();
    let { name, value, type } = e.target;
    if (type === 'number') value *= 1;

    setState((prev) => ({
      ...prev,
      basket: {
        ...prev.basket,
        weight: {
          ...prev.basket.weight,
          [name]: value,
        },
      },
    }));
  };

  // # HANDLE PERSONS INPUTS CHANGES
  // TODO : NEED REFACTORING
  const handleInputsPersonsChange = (e) => {
    e.preventDefault();
    let { name, value, type } = e.target;
    if (type === 'number') value *= 1;

    setState((prev) => ({
      ...prev,
      basket: {
        ...prev.basket,
        persons: {
          ...prev.basket.persons,
          [name]: value,
        },
      },
    }));
  };

  // # HANDLE AVAILABILITY INPUTS CHANGES
  // TODO : NEED REFACTORING
  const handleInputsAvailabilityChange = (e) => {
    e.preventDefault();
    let { name, value, type } = e.target;
    if (type === 'number') value *= 1;

    setState((prev) => ({
      ...prev,
      basket: {
        ...prev.basket,
        availability: {
          ...prev.basket.availability,
          [name]: value,
        },
      },
    }));
  };

  // # HANDLE EDITOR CHANGES
  const onEditorStateChange = (editorState) => {
    setState((prev) => ({
      ...prev,
      basket: {
        ...prev.basket,
        description: editorState,
      },
    }));
  };

  // # HANDLE AUTOCOMPLETE MULTIPLE CHANGES
  const handleAutocompleteMultiple = (type, value) => {
    if (type === 'product') {
      const formatedValues = value.map((val) => ({
        productId: val.productId || val.id,
        photo: val.photo,
        name: val.name,
        reference: val.reference,
        weight: {
          quantity: val.weight?.quantity,
          unity: val.weight?.unity?.name || val.weight?.unity,
          abbreviation: val.weight?.unity?.abbreviation || val.weight?.abbreviation,
        },
        origin: val.origin,
        labels: {
          organic: val.labels?.organic,
          wild: val.labels?.wild,
          french: val.labels?.french,
          native: val.labels?.native,
        },
      }));

      setState((prev) => ({
        ...prev,
        basket: {
          ...prev.basket,
          products: [...formatedValues],
        },
      }));
    }

    if (type === 'tag') {
      setState((prev) => ({
        ...prev,
        basket: {
          ...prev.basket,
          tags: value,
        },
      }));
    }
  };

  // # HANDLE DRAG END
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setState((prev) => ({
        ...prev,
        basket: {
          ...prev.basket,
          products: arrayMove(prev.basket.products, active.id, over.id),
        },
      }));
    }
  };

  // # HANDLE PRODUCTS CHANGES
  const handleProductsChange = (event, productIdx) => {
    let { name, value, type, checked } = event.target;
    if (type === 'number') value *= 1;

    if (type === 'checkbox') {
      const newArr = [...state.basket.products];
      newArr[productIdx].labels[name] = checked;

      setState((prev) => ({
        ...prev,
        basket: {
          ...prev.basket,
          products: newArr,
        },
      }));
    }

    if (name === 'origin' || name === 'name') {
      const newArr = [...state.basket.products];
      newArr[productIdx][name] = value;

      setState((prev) => ({
        ...prev,
        basket: {
          ...prev.basket,
          products: newArr,
        },
      }));
    }

    if (name === 'quantity') {
      const newArr = [...state.basket.products];
      newArr[productIdx].weight.quantity = value;

      setState((prev) => ({
        ...prev,
        basket: {
          ...prev.basket,
          products: newArr,
        },
      }));
    }

    if (name === 'unity') {
      const matchingUnity = state.weightUnities.find((unity) => unity.id === value);
      const newArr = [...state.basket.products];

      newArr[productIdx].weight.unity = matchingUnity.name;
      newArr[productIdx].weight.abbreviation = matchingUnity.abbreviation;

      setState((prev) => ({
        ...prev,
        basket: {
          ...prev.basket,
          products: newArr,
        },
      }));
    }
  };

  // # HANDLE SUBMIT UPDATES
  const handleSubmitUpdates = (e) => {
    e.preventDefault();
    const token = Cookies.get('jwt');

    const formData = new FormData();

    if (state.basket.newPhoto) {
      formData.append('photo', state.basket.newPhoto);
    }

    formData.append('id', state.basket.id);
    formData.append('name', state.basket.name);
    formData.append('reference', state.basket.reference);
    formData.append('price', state.basket.price);
    formData.append('tvaRate', state.basket.tvaRate);
    formData.append('description', draftToHtml(convertToRaw(state.basket.description.getCurrentContent())));
    formData.append('category', state.basket.category);
    formData.append('subCategory', state.basket.subCategory);
    formData.append('isActive', state.basket.isActive);
    formData.append('isTop', state.basket.isTop);
    formData.append('isEco', state.basket.isEco);
    formData.append('size', state.basket.size);
    formData.append('stock', state.basket.stock);
    formData.append('weightFrom', state.basket.weight.from);
    formData.append('weightTo', state.basket.weight.to);
    formData.append('weightUnity', state.basket.weight.unity);
    formData.append('personsFrom', state.basket.persons.from);
    formData.append('personsTo', state.basket.persons.to);
    formData.append('availabilityFrom', state.basket.availability.from);
    formData.append('availabilityTo', state.basket.availability.to);
    formData.append('origin', state.basket.origin);
    formData.append('organic', state.basket.labels.organic);
    formData.append('wild', state.basket.labels.wild);
    formData.append('french', state.basket.labels.french);
    formData.append('native', state.basket.labels.native);
    formData.append('products', JSON.stringify(state.basket.products));
    formData.append('tags', JSON.stringify(state.basket.tags));

    axiosPatchRequest('/baskets/', token, formData)
      .then(() => {
        history.goBack();
      })
      .catch((err) => {
        const { errors } = err.response.data.error;
        for (const error in errors) {
          enqueueSnackbar(`${errors[error].message}`, {
            variant: 'error',
          });
        }
      });
  };

  return (
    <>
      <h1>Panier ID : {basketId}</h1>

      {state.loading && <Skeleton variant="rect" height={200} />}

      {!state.loading && (
        <Grid container spacing={2}>
          <Grid item xs={7}>
            <Paper className={classes.paper}>
              <section className={classes.spaceBetween}>
                <div className={classes.flexCenter}>
                  {state.basket.newPhoto ? (
                    <img src={URL.createObjectURL(state.basket.newPhoto)} alt="upload" width={250} />
                  ) : (
                    <img src={`${process.env.REACT_APP_AWS}/${state.basket.photo}`} alt="upload" width={250} />
                  )}
                  <Input type="file" onChange={handleFileChange} />
                </div>
                <Divider className={classes.divider} orientation="vertical" flexItem />
                <div>
                  <h4>Visibilité :</h4>
                  <div className={classes.flex}>
                    <FormControlLabel
                      control={
                        <Switch
                          name="isActive"
                          color="primary"
                          checked={state.basket.isActive}
                          onChange={handleSwitchChange}
                        />
                      }
                      label="Actif"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          name="isTop"
                          color="primary"
                          checked={state.basket.isTop}
                          onChange={handleSwitchChange}
                        />
                      }
                      label="Top"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          name="isEco"
                          color="primary"
                          checked={state.basket.isEco}
                          onChange={handleSwitchChange}
                        />
                      }
                      label="Eco-acteurs"
                    />
                  </div>
                  <Divider className={classes.divider} />
                  <div>
                    <h4>Labels :</h4>
                    <div>
                      <FormControlLabel
                        control={
                          <Switch
                            name="organic"
                            color="primary"
                            checked={state.basket.labels.organic}
                            onChange={handleSwitchLabelsChange}
                          />
                        }
                        label="Bio"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            name="wild"
                            color="primary"
                            checked={state.basket.labels.wild}
                            onChange={handleSwitchLabelsChange}
                          />
                        }
                        label="Sauvage"
                      />
                    </div>
                    <div>
                      <FormControlLabel
                        control={
                          <Switch
                            name="french"
                            color="primary"
                            checked={state.basket.labels.french}
                            onChange={handleSwitchLabelsChange}
                          />
                        }
                        label="Français"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            name="native"
                            color="primary"
                            checked={state.basket.labels.native}
                            onChange={handleSwitchLabelsChange}
                          />
                        }
                        label="Local"
                      />
                    </div>
                  </div>
                </div>
              </section>
              <Divider className={classes.divider} />
              <section className={classes.infos}>
                <h3>Informations :</h3>
                <div>
                  <TextField
                    name="name"
                    label="Nom"
                    type="text"
                    variant="outlined"
                    value={state.basket.name}
                    onChange={handleInputsChange}
                  />
                  <TextField
                    name="reference"
                    label="Référence"
                    type="text"
                    variant="outlined"
                    value={state.basket.reference}
                    onChange={handleInputsChange}
                  />
                </div>
                <div>
                  <FormControl variant="outlined">
                    <InputLabel>Rubrique</InputLabel>
                    <Select
                      native
                      label="Rubrique"
                      name="category"
                      value={state.basket.category}
                      onChange={handleInputsChange}
                    >
                      <option value="" />
                      {state.categories.map((category, idx) => (
                        <option key={idx} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl variant="outlined">
                    <InputLabel>Sous-rubrique</InputLabel>
                    <Select
                      native
                      label="Sous-rubrique"
                      name="subCategory"
                      value={state.basket.subCategory}
                      onChange={handleInputsChange}
                    >
                      <option value="" />
                      {state.subCategories.map((subCategory, idx) => (
                        <option key={idx} value={subCategory.id}>
                          {subCategory.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                <div>
                  <TextField
                    name="price"
                    label="Prix"
                    type="number"
                    variant="outlined"
                    value={state.basket.price}
                    onChange={handleInputsChange}
                  />
                  <FormControl variant="outlined">
                    <InputLabel>TVA</InputLabel>
                    <Select
                      native
                      name="tvaRate"
                      label="TVA"
                      value={state.basket.tvaRate}
                      onChange={handleInputsChange}
                    >
                      <option value="" />
                      {state.taxes.map((tax, idx) => (
                        <option key={idx} value={tax.id}>
                          {tax.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    name="stock"
                    label="Stock"
                    type="number"
                    variant="outlined"
                    value={state.basket.stock}
                    onChange={handleInputsChange}
                  />
                </div>
                <div>
                  <TextField
                    name="from"
                    label="Poids de"
                    type="number"
                    variant="outlined"
                    value={state.basket.weight.from}
                    onChange={handleInputsWeightChange}
                  />
                  <TextField
                    name="to"
                    label="à"
                    type="number"
                    variant="outlined"
                    value={state.basket.weight.to}
                    onChange={handleInputsWeightChange}
                  />
                  <FormControl variant="outlined">
                    <InputLabel>Unité</InputLabel>
                    <Select
                      native
                      name="unity"
                      label="Unité"
                      value={state.basket.weight.unity}
                      onChange={handleInputsWeightChange}
                    >
                      <option value="" />
                      {state.weightUnities.map((unity, idx) => (
                        <option key={idx} value={unity.id}>
                          {unity.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl variant="outlined">
                    <InputLabel>Taille</InputLabel>
                    <Select native name="size" label="Taille" value={state.basket.size} onChange={handleInputsChange}>
                      <option value="S">S</option>
                      <option value="M">M</option>
                      <option value="L">L</option>
                      <option value="XL">XL</option>
                      <option value="SOLO">Solo</option>
                    </Select>
                  </FormControl>
                </div>
                <h4>Nombre de personnes :</h4>
                <div>
                  <TextField
                    name="from"
                    label="Pour"
                    type="number"
                    variant="outlined"
                    value={state.basket.persons.from}
                    onChange={handleInputsPersonsChange}
                  />
                  <TextField
                    name="to"
                    label="à"
                    type="number"
                    variant="outlined"
                    value={state.basket.persons.to}
                    onChange={handleInputsPersonsChange}
                  />
                </div>
                <h4>Disponibilité :</h4>
                <div>
                  <TextField
                    name="from"
                    label="Du"
                    type="date"
                    variant="outlined"
                    value={state.basket.availability.from}
                    onChange={handleInputsAvailabilityChange}
                  />
                  <TextField
                    name="to"
                    label="au"
                    type="date"
                    variant="outlined"
                    value={state.basket.availability.to}
                    onChange={handleInputsAvailabilityChange}
                  />
                </div>
              </section>
            </Paper>
          </Grid>
          <Grid item xs={5}>
            <Button
              variant="contained"
              color="primary"
              className={classes.marginBottom}
              fullWidth
              onClick={handleSubmitUpdates}
            >
              Modifier le panier
            </Button>
            <Paper className={classes.paper}>
              <h3>Description :</h3>
              <Editor
                editorState={state.basket.description}
                wrapperClassName="wrapper-class"
                editorClassName="editor-class"
                toolbarClassName="toolbar-class"
                toolbar={{
                  options: ['inline', 'colorPicker', 'link'],
                  inline: {
                    options: ['bold', 'italic'],
                  },
                }}
                editorStyle={{
                  border: '1px solid #F1F1F1',
                  minHeight: '150px',
                  padding: '0 1rem',
                  marginBottom: '1rem',
                }}
                onEditorStateChange={onEditorStateChange}
              />
            </Paper>
            <Paper className={classes.paper}>
              <h3>Ajouter des produits :</h3>
              <Autocomplete
                multiple
                value={state.basket.products}
                onChange={(event, value) => handleAutocompleteMultiple('product', value)}
                options={state.products.sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter))}
                groupBy={(option) => option.firstLetter}
                classes={{
                  option: classes.option,
                }}
                autoHighlight
                getOptionLabel={(option) =>
                  `${option.name} - ${option.reference} - ${option.weight.quantity} ${option.weight.abbreviation}`
                }
                renderOption={(option) => (
                  <>
                    {option.name} - {option.reference} - {option.weight.quantity} {option.weight.unity.abbreviation}
                  </>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Produits"
                    variant="outlined"
                    style={{
                      width: '100%',
                      marginBottom: '1rem',
                    }}
                  />
                )}
              />
            </Paper>

            <Paper className={classes.paper}>
              <h3>Ajouter des tags :</h3>
              <Autocomplete
                multiple
                value={state.basket.tags}
                onChange={(event, value) => handleAutocompleteMultiple('tag', value)}
                options={state.tags.sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter))}
                groupBy={(option) => option.firstLetter}
                classes={{
                  option: classes.option,
                }}
                autoHighlight
                getOptionLabel={(option) => `${option.name}`}
                renderOption={(option) => <>{option.name}</>}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tags"
                    variant="outlined"
                    style={{
                      width: '100%',
                      marginBottom: '1rem',
                    }}
                  />
                )}
              />
            </Paper>
          </Grid>

          {state.basket.products.length > 0 && (
            <Grid item xs={12}>
              <Paper className={classes.paper}>
                <h3>Produits dans le panier :</h3>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell align="left">
                            <b>Ordre</b>
                          </TableCell>
                          <TableCell align="left">
                            <b>Image</b>
                          </TableCell>
                          <TableCell align="left">
                            <b>Nom</b>
                          </TableCell>
                          <TableCell align="left">
                            <b>Poids</b>
                          </TableCell>
                          <TableCell align="left">
                            <b>Origine</b>
                          </TableCell>
                          <TableCell align="right">
                            <b>Labels</b>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <SortableContext
                          items={state.basket.products.map((_, index) => index)}
                          strategy={verticalListSortingStrategy}
                        >
                          {state.basket.products.map((product, idx) => (
                            <SortableRow
                              key={idx}
                              product={product}
                              index={idx}
                              classes={classes}
                              state={state}
                              handleProductsChange={handleProductsChange}
                              weightUnities={state.weightUnities}
                            />
                          ))}
                        </SortableContext>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </DndContext>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}
    </>
  );
}
